import { useCallback, useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { PlayoffBracket } from "../types/bracket";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";
import { canonicalRoundDisplayName } from "@sportex100/shared/utils/competitionIdentity.ts";

interface UseBracketResult {
  bracket: PlayoffBracket | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBracket(competitionCode: string, seasonCode: string): UseBracketResult {
  const { acquireToken } = useApiAuth();
  const { isSignedIn: isAuthenticated } = useUser();
  const canLoad = Boolean(isAuthenticated && competitionCode && seasonCode);
  const queryKey = useMemo(() => ["bracket", competitionCode, seasonCode], [competitionCode, seasonCode]);

  const query = useQuery({
    queryKey,
    enabled: canLoad,
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required to fetch bracket.");
      }

      const url = apiUrl(`/api/competitions/${competitionCode}/seasons/${seasonCode}/bracket`);
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bracket: ${response.statusText}`);
      }

      const data: PlayoffBracket = await response.json();
      return {
        ...data,
        rounds: data.rounds.map((round) => ({
          ...round,
          roundName: canonicalRoundDisplayName(round.roundCode, round.roundName) ?? round.roundName,
        })),
      };
    },
  });

  const refetch = useCallback(() => {
    void query.refetch();
  }, [query]);

  return {
    bracket: canLoad ? query.data ?? null : null,
    loading: canLoad ? query.isLoading : false,
    error: canLoad && query.error ? (query.error as Error).message : null,
    refetch,
  };
}
